import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import type { SortToggle } from '@components/SortSelect/SortButton';
import CheckIcon from '@mui/icons-material/Check';
import classNames from 'classnames';
import css from './MobileSortDrawer.module.scss';

export type SortOptionItem<T> = {
  key: string;
  label: string;
  value: T;
};

/**
 * One row of the sheet, shared by the sort options and the optional toggle — same button, label
 * and check glyph, so the two can't diverge visually.
 *
 * `role` differs: options are plain buttons in a list, the toggle is a `checkbox`.
 */
const OptionRow = ({
  label,
  checked,
  role,
  onClick,
}: {
  label: string;
  checked: boolean;
  role?: string;
  onClick: () => void;
}) => (
  <button
    className={classNames(css.option, { [css.selected]: checked })}
    role={role}
    aria-checked={role ? checked : undefined}
    onClick={onClick}
  >
    <span className={css.optionLabel}>{label}</span>
    {checked && <CheckIcon className={css.checkIcon} />}
  </button>
);

type MobileSortDrawerProps<T> = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  groups: SortOptionItem<T>[][];
  selectedKey: string;
  onSelect: (value: T) => void;
  /**
   * Optional checkable row in its own group after the options. Shares `SortButton`'s `SortToggle`
   * type, so the desktop menu and this drawer can't drift apart.
   *
   * Omitted by the Library media page, which must stay exactly as it is.
   */
  toggle?: SortToggle;
};

export const MobileSortDrawer = <T,>({
  open,
  onClose,
  onOpen,
  groups,
  selectedKey,
  onSelect,
  toggle,
}: MobileSortDrawerProps<T>) => {
  return (
    <MobileDrawer open={open} onClose={onClose} onOpen={onOpen} frosted>
      <div className={css.content}>
        <span className={css.title}>Sort by</span>
        {groups.map((group, gi) => (
          <div key={gi} className={css.group}>
            {group.map((option) => (
              <OptionRow
                key={option.key}
                label={option.label}
                checked={selectedKey === option.key}
                onClick={() => {
                  onSelect(option.value);
                  onClose();
                }}
              />
            ))}
          </div>
        ))}
        {toggle && (
          <div className={css.group}>
            {/* Unlike picking a sort, this doesn't close the drawer: it's a modifier the user
                may want to flip while the chosen sort is still visible above it. */}
            <OptionRow
              label={toggle.label}
              checked={toggle.checked}
              role="checkbox"
              onClick={() => toggle.onChange(!toggle.checked)}
            />
          </div>
        )}
      </div>
    </MobileDrawer>
  );
};
